import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X } from "lucide-react";
import {
  ALABAMA_COUNTIES,
  BUDGET_RANGES,
  TIMELINES,
  STAGES,
  FUNDING_SOURCES,
} from "@/lib/alabamaCounties";

interface GrantReadyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ideaId: string;
  ideaTitle: string;
  existing?: any | null;
}

const GrantReadyDialog = ({
  open,
  onOpenChange,
  ideaId,
  ideaTitle,
  existing,
}: GrantReadyDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [problem, setProblem] = useState("");
  const [solution, setSolution] = useState("");
  const [target, setTarget] = useState("");
  const [serviceArea, setServiceArea] = useState<string[]>([]);
  const [countyQuery, setCountyQuery] = useState("");
  const [budget, setBudget] = useState<string>("");
  const [timeline, setTimeline] = useState<string>("");
  const [stage, setStage] = useState<string>("");
  const [partners, setPartners] = useState("");
  const [outcomes, setOutcomes] = useState("");
  const [fundingSought, setFundingSought] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setProblem(existing?.problem_statement ?? "");
      setSolution(existing?.proposed_solution ?? "");
      setTarget(existing?.target_population ?? "");
      setServiceArea(existing?.service_area ?? []);
      setBudget(existing?.budget_range ?? "");
      setTimeline(existing?.timeline ?? "");
      setStage(existing?.stage ?? "");
      setPartners(existing?.partners ?? "");
      setOutcomes(existing?.outcomes ?? "");
      setFundingSought(existing?.funding_sought ?? []);
      setCountyQuery("");
    }
  }, [open, existing]);

  const toggleCounty = (county: string) => {
    setServiceArea((prev) =>
      prev.includes(county) ? prev.filter((c) => c !== county) : [...prev, county]
    );
  };

  const toggleFunding = (source: string) => {
    setFundingSought((prev) =>
      prev.includes(source) ? prev.filter((s) => s !== source) : [...prev, source]
    );
  };

  const filteredCounties = ALABAMA_COUNTIES.filter((c) =>
    c.toLowerCase().includes(countyQuery.toLowerCase())
  );

  const save = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("You must be signed in.");

      const payload = {
        idea_id: ideaId,
        problem_statement: problem.trim() || null,
        proposed_solution: solution.trim() || null,
        target_population: target.trim() || null,
        service_area: serviceArea,
        budget_range: budget || null,
        timeline: timeline || null,
        stage: stage || null,
        partners: partners.trim() || null,
        outcomes: outcomes.trim() || null,
        funding_sought: fundingSought,
        last_edited_by: user.id,
      };

      if (existing?.id) {
        const { error } = await supabase
          .from("idea_grant_details")
          .update(payload)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("idea_grant_details")
          .insert({ ...payload, created_by: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: existing?.id ? "Grant brief updated" : "Grant brief created",
        description: "Funders and agencies can now see this fundable proposal.",
      });
      queryClient.invalidateQueries({ queryKey: ["idea-grant-details", ideaId] });
      queryClient.invalidateQueries({ queryKey: ["ideas"] });
      queryClient.invalidateQueries({ queryKey: ["grant-ready-set"] });
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({
        title: "Could not save",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {existing?.id ? "Edit Grant-Ready Brief" : "Make This Grant-Ready"}
          </DialogTitle>
          <DialogDescription>
            Add the structured details funders and agencies need to evaluate{" "}
            <span className="text-foreground font-medium">"{ideaTitle}"</span>. All
            fields are optional.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <form
            id="grant-ready-form"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
            className="space-y-5 py-2"
          >
            <div>
              <Label className="text-foreground">Problem statement</Label>
              <Textarea
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                placeholder="What community problem does this solve?"
                rows={3}
                maxLength={1000}
                className="mt-1 resize-none"
              />
            </div>

            <div>
              <Label className="text-foreground">Proposed solution</Label>
              <Textarea
                value={solution}
                onChange={(e) => setSolution(e.target.value)}
                placeholder="How does your idea address the problem?"
                rows={4}
                maxLength={1500}
                className="mt-1 resize-none"
              />
            </div>

            <div>
              <Label className="text-foreground">Target population</Label>
              <Input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="e.g. Mobile County families with children under 5"
                maxLength={300}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-foreground">Service area (Alabama counties)</Label>
              {serviceArea.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
                  {serviceArea.map((c) => (
                    <Badge
                      key={c}
                      variant="secondary"
                      className="gap-1 cursor-pointer"
                      onClick={() => toggleCounty(c)}
                    >
                      {c}
                      <X className="h-3 w-3" />
                    </Badge>
                  ))}
                </div>
              )}
              <Input
                value={countyQuery}
                onChange={(e) => setCountyQuery(e.target.value)}
                placeholder="Search counties..."
                className="mt-1"
              />
              <div className="mt-2 max-h-32 overflow-y-auto border border-border rounded-md p-2 grid grid-cols-2 sm:grid-cols-3 gap-1">
                {filteredCounties.map((c) => {
                  const selected = serviceArea.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCounty(c)}
                      className={`text-xs px-2 py-1 rounded text-left transition-colors ${
                        selected
                          ? "bg-primary/15 text-primary"
                          : "text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-foreground">Budget range</Label>
                <Select value={budget} onValueChange={setBudget}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGET_RANGES.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-foreground">Timeline</Label>
                <Select value={timeline} onValueChange={setTimeline}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMELINES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-foreground">Stage</Label>
                <Select value={stage} onValueChange={setStage}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-foreground">Partners / collaborators</Label>
              <Input
                value={partners}
                onChange={(e) => setPartners(e.target.value)}
                placeholder="Organizations you'd partner with"
                maxLength={500}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-foreground">Measurable outcomes</Label>
              <Textarea
                value={outcomes}
                onChange={(e) => setOutcomes(e.target.value)}
                placeholder="How will you measure success?"
                rows={3}
                maxLength={1000}
                className="mt-1 resize-none"
              />
            </div>

            <div>
              <Label className="text-foreground">Funding sought</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {FUNDING_SOURCES.map((source) => (
                  <label
                    key={source}
                    className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer"
                  >
                    <Checkbox
                      checked={fundingSought.includes(source)}
                      onCheckedChange={() => toggleFunding(source)}
                    />
                    {source}
                  </label>
                ))}
              </div>
            </div>
          </form>
        </ScrollArea>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={save.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="grant-ready-form"
            disabled={save.isPending}
          >
            {save.isPending
              ? "Saving..."
              : existing?.id
                ? "Update Brief"
                : "Save Brief"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GrantReadyDialog;